import { getExperienceLabel } from "../jobs/Jobs";
import CodeHighlighter from "../../components/CodeHighlighter";
import { useSearchParams } from "react-router-dom";
import DocViewer, { DocViewerRenderers } from "react-doc-viewer";
import FileViewer from "../marketplace/FileViewer";

const RenderMedia = ({ post }) => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type"); 
  if (post.type === "image" && post.image) {
    return (
      <div className="w-full h-full min-h-0 overflow-hidden">
        <FileViewer
          publication_type="image"
          file={post.image}
          name={post.title || post.description || "Post"}
          contained
        />
      </div>
    );
  }

  // Video
  if (post.type === "video" && post.video) {
    return (
      <div className="w-full h-full min-h-0 overflow-hidden">
        <FileViewer
          publication_type="video"
          file={post.video}
          name={post.title || "Video"}
          contained
        />
      </div>
    );
  }

  // Code
  if (post.type === "code") {
    return post.code ? (
      <pre className="w-full h-full max-h-full  text-gray-100 rounded-lg font-mono text-sm overflow-auto break-words whitespace-pre-wrap">
         <CodeHighlighter codeHtml={post.code} padding='p-8'/>
     </pre>
     
     
    ) : (
      <pre className="w-full max-h-[600px] overflow-auto bg-[#222] text-[#eee] rounded-lg font-['Fira_Code,_monospace'] text-sm p-4">
        // No code
      </pre>
    );
  }

  // Uploaded document
  if (post.upload_document) {
    const fileName = post.upload_document?.split("/").pop() || "unknown";
    const ext = fileName.includes(".") ? fileName.split(".").pop().toLowerCase() : "";
    let fileTypeLabel = "Document";
    if (ext === "pdf") fileTypeLabel = "PDF Document";
    else if (["doc", "docx"].includes(ext)) fileTypeLabel = "Word Document";
    else if (["xls", "xlsx"].includes(ext)) fileTypeLabel = "Excel Spreadsheet";
    else if (["ppt", "pptx"].includes(ext)) fileTypeLabel = "PowerPoint Presentation";
    else if (ext === "txt") fileTypeLabel = "Text File";
    return (
      <div className="w-full h-full bg-white p-4 rounded-lg break-words flex flex-col gap-4 overflow-auto">
  {/* Title */}
  {post.title && (
    <h1 className="text-xl font-bold text-gray-900">{post.title}</h1>
  )}

 {/* Description & Abstract */}
  {(post.description || post.absract) && (
    <div className="flex-1 overflow-auto max-h-[200px] space-y-2">
      {post.description && (
        <p className="text-gray-700">{post.description}</p>
      )}
      {post.absract && (
        <p className="text-gray-700">{post.absract}</p>
      )}
    </div>
  )}
  { /\.pdf(\?.*)?$/i.test(post.upload_document || '') ? (
  <iframe
    src={post.upload_document}
    title={post?.title || "PDF Viewer"}
    className="w-full h-full max-h-full rounded-lg object-cover"
  />
) : /\.(png|jpe?g|gif)(\?.*)?$/i.test(post.upload_document || '') ? (
  <FileViewer
    publication_type="image"
    file={post.upload_document}
    name={post?.title || "Image Viewer"}
  />
) : /\.html?(\?.*)?$/i.test(post.upload_document || '') ? (
  <iframe 
    src={post.upload_document}
    title={post?.title || "HTML Viewer"}
    className="w-full h-full max-h-full rounded-lg object-cover"
  />
) : (
  <DocViewer
     documents={[
      {
        uri: post.upload_document,
        fileName: post?.title || "Document",
      },
    ]}
    pluginRenderers={DocViewerRenderers}
    className="w-full h-full max-h-full rounded-lg object-cover"
    config={{
      header: {
        disableHeader: true,
        disableFileName: true,
      },
    }}
  />
)}
</div>
    );
  }

  // Normal content from URL type
 if (type === "normal") {
  return (
    <div className="w-full h-full p-4 bg-white border border-gray-300 rounded-lg break-words overflow-auto">
      {post.title && <h3 className="text-lg font-semibold mb-2">{post.title}</h3>}
      {post.content && <div dangerouslySetInnerHTML={{ __html: post.content }} />}
    </div>
  );
}

  // Post or job fallback
if (post.post_type === "post" || post.post_type === "job") {
  return (
    <div className="w-full h-full bg-white p-4 border border-gray-300 rounded-lg break-words overflow-auto">
      {post.title && <h3 className="text-lg font-semibold mb-2">{post.title}</h3>}
      {post.content && <p className="mb-4"> {post.content}</p>}

      {post.post_type === "job" && (
        <div className="mt-6 pt-6 border-t border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* Left Column */}
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-500">Job Title</span>
                <p className="text-gray-900">{post.job_title}</p>
              </div>

              <div>
                <span className="text-sm font-medium text-gray-500">Company</span>
                <p className="text-gray-900">{post.company_name}</p>
              </div>

              <div>
                <span className="text-sm font-medium text-gray-500">Location</span>
                <p className="text-gray-900">{post.location}</p>
              </div>

              <div>
                <span className="text-sm font-medium text-gray-500">Remote</span>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    post.remote_allowed ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {post.remote_allowed ? "Yes" : "No"}
                </span>
              </div>

              <div>
                <span className="text-sm font-medium text-gray-500">Experience</span>
                <p className="text-gray-900">{getExperienceLabel(post.experience_level)}</p>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-500">Salary</span>
                <p className="text-gray-900">
                  {post.salary_min && post.salary_max 
                    ? `${post.salary_min} - ${post.salary_max} ${post.currency}`
                    : "Not specified"}
                </p>
              </div>

              {post.skills_list?.length > 0 && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Skills</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {post.skills_list.map((skill, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {post.qualifications && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Qualifications</span>
                  <p className="text-gray-900">{post.qualifications}</p>
                </div>
              )}

              <div>
                <span className="text-sm font-medium text-gray-500">Application</span>
                <div className="space-y-2 mt-2">
                  {post.application_email && (
                    <a
                      href={`mailto:${post.application_email}`}
                      className="block text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {post.application_email}
                    </a>
                  )}
                  {post.application_url && (
                    <a
                      href={post.application_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      Apply Online →
                    </a>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
  // Fallback
  return <div className="p-5 bg-gray-50 rounded">No media available.</div>;
};

export default RenderMedia;